/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Alert, CircularProgress } from "@mui/material";
import { HlsVideoProps } from "hooks/useHlsVideo";
import { useTenantTranslation } from "hooks/formatters";
import {
  useHistoricalAngleVideoUrl,
  useHistoricalCameraVideoUrl,
} from "components/scrapEyeVideoPlayer/hooks";
import { SynchronizedHlsVideo } from "./SynchronizedHlsVideo";
import { DEFAULT_VIDEO_ASPECT_RATIO } from "../constants";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";

export const HlsAngleVideoPlayer = ({
  location,
  angle,
  startIso,
  endIso,
  videoIndex = 0,
  deviceId,
  ...props
}: {
  location: string;
  angle: string;
  startIso: string | null;
  endIso: string | null;
  videoIndex?: number;
  deviceId?: number;
} & Omit<HlsVideoProps, "srcUrl" | "playbackRate" | "autoPlay">) => {
  const { t } = useTenantTranslation();

  const {
    url: hlsUrl,
    isLoading,
    error,
  } = useHistoricalAngleVideoUrl(location, angle, startIso, endIso, deviceId);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed #ddd",
          borderRadius: 8,
          aspectRatio: DEFAULT_VIDEO_ASPECT_RATIO,
          width: "100%",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return <Alert severity="error">{t("videoStreamError")}</Alert>;
  }

  if (!hlsUrl) {
    return null;
  }

  return (
    <EnsureGcsTokenExists>
      <SynchronizedHlsVideo
        srcUrl={hlsUrl}
        videoId={`${location}-${angle}-${videoIndex}`}
        videoIndex={videoIndex}
        expectedStartIso={startIso ?? undefined}
        autoPlay={false}
        {...props}
      />
    </EnsureGcsTokenExists>
  );
};

export const HlsCameraVideoPlayer = ({
  deviceId,
  physicalId,
  startIso,
  endIso,
  videoIndex = 0,
  ...props
}: {
  deviceId: number;
  physicalId: string;
  startIso: string | null;
  endIso: string | null;
  videoIndex?: number;
} & Omit<HlsVideoProps, "srcUrl" | "playbackRate" | "autoPlay">) => {
  const { t } = useTenantTranslation();

  const {
    url: hlsUrl,
    isLoading,
    error,
  } = useHistoricalCameraVideoUrl(deviceId, physicalId, startIso, endIso);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed #ddd",
          borderRadius: 8,
          aspectRatio: DEFAULT_VIDEO_ASPECT_RATIO,
          width: "100%",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return <Alert severity="error">{t("videoStreamError")}</Alert>;
  }

  if (!hlsUrl) {
    return null;
  }

  return (
    <EnsureGcsTokenExists>
      <SynchronizedHlsVideo
        srcUrl={hlsUrl}
        videoId={`device-${deviceId}-camera-${physicalId}-${videoIndex}`}
        videoIndex={videoIndex}
        expectedStartIso={startIso ?? undefined}
        autoPlay={false}
        {...props}
      />
    </EnsureGcsTokenExists>
  );
};
